import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { crmService } from '../../services/crm.service';
import { crmErrorMessage, personDisplayName, primaryValue } from '../../utils/crm';

const MAX_SUBJECT = 200;
const MAX_BODY = 10000;

/**
 * Sends an email to a contact, in the agent's name (replies come to the agent). The address is never
 * sent from the browser: the CRM reads it from the encrypted record. A sent email is recorded on the
 * contact's timeline and counts as being in touch.
 */
const EmailDialog = ({ book, ownerUserId, person, side, onClose, onSent }) => {
  const displayName = personDisplayName(person);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!subject.trim() || !body.trim()) {
      setError('Write a subject and a message.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await crmService.sendEmail(book, person.id, {
        subject: subject.trim(),
        body: body.trim(),
        ...(side ? { side } : {}),
      }, ownerUserId);
      setDone(result);
      onSent();
    } catch (err) {
      const status = err?.response?.status;
      // The CRM's own sentence for a refusal (no address, lost lead, daily limit); the shared helper's 409 text is about duplicates.
      const reason = err?.response?.data?.message;
      if ((status === 400 || status === 409) && reason) setError(reason);
      else if (status === 401 || status === 403 || status === 404) setError(crmErrorMessage(err));
      else setError('The email could not be sent. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="crm-modal-backdrop crm-modal-top">
      <form className="crm-modal" role="dialog" aria-modal="true" aria-label="Send email" onSubmit={submit}>
        <h2>Email {displayName}</h2>
        {done ? (
          <>
            <p>Email sent to {primaryValue(person.emails)}. Replies will come to you.</p>
            {!done.logged && (
              <p className="crm-error" role="alert">It was sent, but it could not be added to the timeline. Log it as a note if you want a record.</p>
            )}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn crm-btn-primary" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <p className="crm-note">To: {primaryValue(person.emails)}. Sent as you, and recorded on their timeline.</p>
            <label>Subject
              <input className="crm-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={MAX_SUBJECT} autoFocus />
            </label>
            <label>Message
              <textarea className="crm-input" rows={9} value={body} onChange={(e) => setBody(e.target.value)} maxLength={MAX_BODY} />
            </label>
            {error && <p className="crm-error" role="alert">{error}</p>}
            <div className="crm-modal-actions">
              <button type="button" className="crm-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="crm-btn crm-btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
            </div>
          </>
        )}
      </form>
    </div>,
    document.body
  );
};

export default EmailDialog;
